import React from 'react';
import MediaDisplay from '../MediaDisplay/MediaDisplay.tsx';

interface MediaAttachment {
  file_path: string;
  media_type: 'image' | 'video' | 'audio';
  filename: string;
  content_type: string;
  model_used?: string;
  generated_at?: string;
  metadata?: Record<string, any>;
}

interface MessageMedia {
  type: 'text' | 'media' | 'mixed';
  content: string;
  media_attachments?: MediaAttachment[];
  metadata?: Record<string, any>;
}

interface EnhancedMessageContentProps {
  message: MessageMedia;
  theme?: 'light' | 'dark';
  className?: string;
}

const EnhancedMessageContent: React.FC<EnhancedMessageContentProps> = ({
  message,
  theme = 'light',
  className = ''
}) => {
  const renderContent = () => {
    switch (message.type) {
      case 'text':
        return (
          <div className={`
            whitespace-pre-wrap leading-6 sm:leading-7 font-inter font-medium text-base
            ${theme === 'dark' ? 'text-ink-white' : 'text-ink-ink'}
          `}>
            {message.content}
          </div>
        );

      case 'media':
        return (
          <div className="space-y-4">
            {/* Show prompt/description if available */}
            {message.content && (
              <div className={`
                text-sm mb-3 p-3 rounded-lg border-l-4 border-edge-blue-500
                ${theme === 'dark'
                  ? 'bg-fill-gray-800 text-ink-gray-300 border-l-edge-blue-400'
                  : 'bg-fill-blue-50 text-ink-gray-700 border-l-edge-blue-500'
                }
              `}>
                <span className="font-medium">Prompt:</span> {message.content}
              </div>
            )}

            {/* Render media attachments */}
            <div className="grid gap-4">
              {message.media_attachments?.map((media, index) => (
                <MediaDisplay
                  key={`${media.file_path}-${index}`}
                  media={media}
                  theme={theme}
                  size="md"
                  showMetadata={true}
                  className="w-full"
                />
              ))}
            </div>
          </div>
        );

      case 'mixed':
        return (
          <div className="space-y-4">
            {/* Text content */}
            {message.content && (
              <div className={`
                whitespace-pre-wrap leading-6 sm:leading-7 font-inter font-medium text-base
                ${theme === 'dark' ? 'text-ink-white' : 'text-ink-ink'}
              `}>
                {message.content}
              </div>
            )}

            {/* Media attachments */}
            {message.media_attachments && message.media_attachments.length > 0 && (
              <div className="grid gap-4 mt-4">
                {message.media_attachments.map((media, index) => (
                  <MediaDisplay
                    key={`${media.file_path}-${index}`}
                    media={media}
                    theme={theme}
                    size="md"
                    showMetadata={false}
                    className="w-full"
                  />
                ))}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className={`
            whitespace-pre-wrap leading-6 sm:leading-7 font-inter font-medium text-base
            ${theme === 'dark' ? 'text-ink-white' : 'text-ink-ink'}
          `}>
            {message.content}
          </div>
        );
    }
  };

  return (
    <div className={`message-content ${className}`}>
      {renderContent()}

      {/* Metadata for debugging (only shown when needed) */}
      {message.metadata && window.location.hostname === 'localhost' && (
        <details className="mt-4">
          <summary className={`
            text-xs cursor-pointer
            ${theme === 'dark' ? 'text-ink-gray-400' : 'text-ink-gray-500'}
          `}>
            Debug Info
          </summary>
          <pre className={`
            text-xs mt-2 p-2 rounded overflow-auto
            ${theme === 'dark' ? 'bg-fill-gray-800 text-ink-gray-300' : 'bg-fill-gray-100 text-ink-gray-600'}
          `}>
            {JSON.stringify(message.metadata, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

export default EnhancedMessageContent;
